import React from 'react';
import { Box, Button, Chip, Grid, Link, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import moment from 'moment';

import { DataAPIContext, CREATE, GET_ONE } from '../api';
import { useSnackbar } from 'notistack';

import { ContextStore } from '../store/contextStore';
import TaskLogViewer from './TaskLogViewer';
import TaskProgressBar from './TaskProgressBar';

const STATE_COLORS = {
    0: 'default',
    1: 'primary',
    2: 'success',
    3: 'error',
    4: 'warning',
    5: 'info',
};

const TaskDetailPanel = ({ task, onRefresh }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const { isFranchise } = React.useContext(ContextStore);
    const [showLogs, setShowLogs] = React.useState(false);
    const logViewerRef = React.useRef(null);
    const [artifactUrls, setArtifactUrls] = React.useState({});
    const [deleteResult, setDeleteResult] = React.useState(null);

    // A bulk delete finishes "Completed" even when records could not be deleted (still in use), so
    // its outcome comes from the result (CP-7769, CP-7771): {deleted, errors: [{id, error}], total}.
    const isFinishedBatchDelete = task.task_type === 'batch_delete' && task.state === 2;
    React.useEffect(() => {
        if (!isFinishedBatchDelete) return undefined;
        let live = true;
        dataAPI(GET_ONE, 'bg_tasks', { id: task.id, urlSuffix: 'result/' }).then((res) => {
            if (live && res?.statusCode === 200 && res.data?.result) {
                setDeleteResult(res.data.result);
            }
        }).catch(() => {});
        return () => {
            live = false;
        };
    }, [isFinishedBatchDelete, task.id, dataAPI]);

    // Fetch presigned URLs for artifacts
    React.useEffect(() => {
        if (!task.has_artifacts && !(task.artifacts?.length > 0)) return;
        const artifacts = task.artifacts || [];
        artifacts.forEach((artifact) => {
            dataAPI(GET_ONE, 'bg_tasks', {
                urlSuffix: `${task.id}/artifact_url/?name=${encodeURIComponent(artifact.name)}`,
            }).then((res) => {
                if (res && res.statusCode === 200 && res.data?.url) {
                    setArtifactUrls((prev) => ({ ...prev, [artifact.name]: res.data.url }));
                }
            }).catch(() => {});
        });
    }, [task.id, task.artifacts, task.has_artifacts, dataAPI]);

    const handleCopyLogs = () => {
        const text = logViewerRef.current?.getLogText();
        if (!text) {
            enqueueSnackbar('No logs to copy', { variant: 'warning' });
            return;
        }
        navigator.clipboard.writeText(text).then(() => {
            enqueueSnackbar('Logs copied to clipboard', { variant: 'success' });
        }).catch(() => {
            enqueueSnackbar('Failed to copy logs', { variant: 'error' });
        });
    };

    const handleDownloadLogs = () => {
        const text = logViewerRef.current?.getLogText();
        if (!text) {
            enqueueSnackbar('No logs to download', { variant: 'warning' });
            return;
        }
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `task-${task.id}-logs.txt`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const handleCancel = () => {
        dataAPI(CREATE, 'bg_tasks', { urlSuffix: `${task.id}/cancel/`, data: {} }).then((res) => {
            if (res && res.statusCode === 200) {
                enqueueSnackbar('Cancellation requested', { variant: 'info' });
                onRefresh && onRefresh();
            } else {
                enqueueSnackbar(res?.data?.message || 'Failed to cancel', { variant: 'error' });
            }
        }).catch(() => {
            enqueueSnackbar('Failed to cancel task', { variant: 'error' });
        });
    };

    const handleApprove = () => {
        dataAPI(CREATE, 'bg_tasks', { urlSuffix: `${task.id}/approve/`, data: {} }).then((res) => {
            if (res && res.statusCode === 200) {
                enqueueSnackbar('Step approved, task resuming', { variant: 'success' });
                onRefresh && onRefresh();
            } else {
                enqueueSnackbar(res?.data?.message || 'Failed to approve', { variant: 'error' });
            }
        }).catch(() => {
            enqueueSnackbar('Failed to approve step', { variant: 'error' });
        });
    };

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Type</Typography>
                    <Typography variant="body2">{task.task_type}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Status</Typography>
                    <Box>
                        <Chip
                            label={task.state_display}
                            size="small"
                            color={STATE_COLORS[task.state] || 'default'}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Started by</Typography>
                    <Typography variant="body2">{task.user_email || '-'}</Typography>
                </Grid>
                {isFranchise && task.tour_operator?.name && (
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Licensee</Typography>
                        <Typography variant="body2">{task.tour_operator.name}</Typography>
                    </Grid>
                )}
                <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Duration</Typography>
                    <Typography variant="body2">
                        {task.duration_seconds != null
                            ? `${task.duration_seconds.toFixed(1)}s`
                            : task.started_ts
                            ? `${moment().diff(moment(task.started_ts), 'seconds')}s (running)`
                            : '-'}
                    </Typography>
                </Grid>
                {task.state === 1 && task.progress && (
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Progress</Typography>
                        <TaskProgressBar progress={task.progress} />
                    </Grid>
                )}
                {task.error_message && (
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Error</Typography>
                        <Typography variant="body2" color="error">{task.error_message}</Typography>
                    </Grid>
                )}
                {deleteResult && (
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Result</Typography>
                        <Typography variant="body2" color={deleteResult.deleted < deleteResult.total ? 'error' : 'inherit'}>
                            {deleteResult.total === 0
                                ? 'Nothing was deleted'
                                : `Deleted ${deleteResult.deleted} of ${deleteResult.total}`}
                        </Typography>
                        {(deleteResult.errors || []).slice(0, 20).map((err) => (
                            <Typography key={err.id} variant="body2" color="textSecondary">
                                ID {err.id}: {err.error}
                            </Typography>
                        ))}
                        {(deleteResult.errors || []).length > 20 && (
                            <Typography variant="body2" color="textSecondary">
                                …and {deleteResult.errors.length - 20} more (see the logs)
                            </Typography>
                        )}
                    </Grid>
                )}
                {task.artifacts?.length > 0 && (
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">Artifacts</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                            {task.artifacts.map((artifact) => (
                                <Box key={artifact.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AttachFileIcon fontSize="small" color="action" />
                                    {artifactUrls[artifact.name] ? (
                                        <Link
                                            href={artifactUrls[artifact.name]}
                                            target="_blank"
                                            rel="noopener"
                                            variant="body2"
                                        >
                                            {artifact.name}
                                        </Link>
                                    ) : (
                                        <Typography variant="body2">{artifact.name}</Typography>
                                    )}
                                    <Typography variant="caption" color="textSecondary">
                                        ({(artifact.size / 1024).toFixed(1)} KB, {artifact.content_type})
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {task.can_cancel && (task.state === 0 || task.state === 1) && (
                            <Button size="small" color="warning" variant="outlined" onClick={handleCancel}>
                                Cancel
                            </Button>
                        )}
                        {/* Awaiting approval: only what this surface allows. Easy-onboarding lane tasks
                            report can_approve / can_cancel false on the dashboard (ops act on them in the
                            console); a backend older than can_approve leaves it undefined: offered as before. */}
                        {task.state === 5 && task.can_approve !== false && (
                            <Button size="small" color="primary" variant="contained" onClick={handleApprove}>
                                Approve Next Step
                            </Button>
                        )}
                        {task.state === 5 && task.can_cancel && (
                            <Button size="small" color="warning" variant="outlined" onClick={handleCancel}>
                                Cancel
                            </Button>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setShowLogs(!showLogs)}
                        >
                            {showLogs ? 'Hide Logs' : 'Show Logs'}
                        </Button>
                        {showLogs && (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ContentCopyIcon fontSize="small" />}
                                    onClick={handleCopyLogs}
                                >
                                    Copy Logs
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon fontSize="small" />}
                                    onClick={handleDownloadLogs}
                                >
                                    Download Logs
                                </Button>
                            </>
                        )}
                    </Box>
                </Grid>
                {showLogs && (
                    <Grid item xs={12}>
                        <TaskLogViewer
                            ref={logViewerRef}
                            taskId={task.id}
                            taskState={task.state}
                            logKey={task.log_key}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default TaskDetailPanel;
